import axios, {type AxiosError, type InternalAxiosRequestConfig} from "axios";
import authTokenService from  "src/services/auth.service";

const baseURL = process.env.REACT_APP_SUMMERYOULAB_API_URL;
if (!baseURL) {
    throw new Error("API base URL is not configured");
}

const apiClient = axios.create({
    baseURL,
    timeout : 30000,
    headers : {
        Accept : "application/json",
        "Content-Type" : "application/json"
    }
});

// Separate client: refreshing must not trigger the same interceptor.
const refreshClient = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

type RetryConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

let refreshPromise: Promise<string> | null = null;

apiClient.interceptors.request.use((config) => {
    const token = authTokenService.getAccessToken();

    if (token) 
        config.headers.set("Authorization", `Bearer ${token}`);

    return config;
})

apiClient.interceptors.response.use(
    (response) => response,
    async (error : AxiosError) => {
        const originalRequest = error.config as RetryConfig | undefined;

        if (error.response?.status !== 401 || !originalRequest || originalRequest._retry)
            return Promise.reject(error);

        // Don't refresh for login/refresh endpoints
        if (originalRequest.url?.includes("auth/refresh-token") || originalRequest.url?.includes("/auth/login"))
            return Promise.reject(error);
            
        originalRequest._retry = true;

        try {
            if (!refreshPromise) {
                refreshPromise = (async () => {
                    const currentRefreshToken = authTokenService.getRefreshToken();
                    
                    if (!currentRefreshToken)
                        throw new Error("Refresh token is unavailable");

                    const response = await refreshClient.post("/auth/refresh-token",
                        {refreshToken : currentRefreshToken }
                    )

                    const newAccessToken: string = response.data.newAccessToken;
                    if (!newAccessToken)
                        throw new Error("Refresh response has no access token.");

                    authTokenService.setTokens(newAccessToken);
                    return newAccessToken;
                })();
            }

            const newAccessToken = await refreshPromise;
            originalRequest.headers.set("Authorization", `Bearer ${newAccessToken}`);
            
            return apiClient(originalRequest);
        }
        catch (refreshError) {
            authTokenService.logOut();
            window.location.href = "/signIn";

            return Promise.reject(refreshError);
        } finally {
            refreshPromise = null;
        }
    }
);

export default apiClient;